<template>
  <el-card class="box-card">
    <div slot="header" class="box-head clearfix">
      <span class="card-head">Scheduled Social Post Calendar</span>
    </div>
    <div class="card-content">
      <full-calendar
        defaultView="dayGridMonth"
        ref="fullCalendar"
        :plugins="calendarPlugins"
        :header="{
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
        }"
        :events="calendarEvents"
        :droppable="true"
        :editable="true"
        :nowIndicator="true"
        :eventRender="handleEventRender"
        :timeZone="'local'"
        @eventClick="handleEventClick"
        @dateClick="handleDateClick"
        @eventDrop="handleEventDrop"
      />
    </div>
  </el-card>
</template>

<script>
import FullCalendar from '@fullcalendar/vue';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';

const channelTypeConfig = {
  2: { icon: 'fa-twitter', color: '#1DA1F2' },
  3: { icon: 'fa-facebook', color: '#4767aa' },
};

export default {
  name: 'SocialPostCalendar',
  components: {
    FullCalendar,
  },
  props: {
    channels: {
      type: Array,
      default: [],
    },
    posts: {
      type: Array,
      default: [],
    },
  },
  data: () => ({
    calendarPlugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
  }),

  computed: {
    calendarEvents() {
      return this.posts.map(p => ({
        id: p.id,
        title: p.message,
        start: new Date(p.post_date),
        icon: 'twitter',
        editable: !p.sent,
        selectable: !p.sent,
        durationEditable: false,
        backgroundColor: this.calculateBackgroundColor(p.channels),
        borderColor: p.sent ? '#f29421' : this.calculateBackgroundColor(p.channels),
        channelIds: p.channels,
      }));
    },
  },

  methods: {
    channelIconsHtml(event) {
      const uniqueChannelIds = [...new Set(event.extendedProps.channelIds)];
      const iconHtmlNodes = uniqueChannelIds.map(id => {
        const config = this.configForChannel(id);
        return config ? `<i class="fa ${config.icon}"></i>&nbsp` : '';
      });
      return iconHtmlNodes.join('');
    },

    channelForId(channelId) {
      return this.channels.find(c => c.id === channelId);
    },

    postForId(postId) {
      return this.posts.find(p => p.id === postId);
    },

    configForChannel(channelId) {
      const channel = this.channelForId(channelId);

      if (!channel) {
        return null;
      }

      const config = channelTypeConfig[channel.channel_type];
      return config;
    },

    calculateBackgroundColor(channelIds) {
      const colors = channelIds.map(id => {
        const config = this.configForChannel(id);
        return config ? config.color : '#000000';
      });

      return colors[colors.length - 1];
    },

    handleEventRender(e) {
      const titleEl = e.el.querySelector('.fc-title');
      titleEl.innerHTML = `${this.channelIconsHtml(e.event)} ${titleEl.innerHTML}`;
    },

    handleDateClick(e) {
      if (e.date < new Date()) {
        this.$notify({
          type: 'warning',
          showClose: true,
          duration: 2500,
          title: 'Date is in the past',
          message: 'Unable to create a new scheduled post in the past.',
        });
        return;
      }
      this.$emit('newPost', e.date);
    },

    handleEventClick(e) {
      const post = this.postForId(parseInt(e.event.id, 10));
      if (post.sent) {
        this.$notify({
          type: 'warning',
          showClose: true,
          duration: 2500,
          title: 'Already sent',
          message: 'Unable to edit a post that has already been sent.',
        });
        return;
      }
      this.$emit('editPost', post);
    },

    handleEventDrop(e) {
      const post = this.postForId(parseInt(e.event.id, 10));

      const data = {
        id: post.id,
        channelIds: post.channels,
        media: post.media,
        message: post.message,
        scheduleTimestamp: e.event.start.getTime() / 1000,
      };

      this.$store
        .dispatch('social/updateScheduledPost', data)
        .then(() => this.$store.dispatch('social/getScheduledMessages'))
        .catch(() => {
          this.$alert('An error occurred');
        });
    },
  },
};
</script>

<style lang="scss">
@import '~@fullcalendar/core/main.css';
@import '~@fullcalendar/daygrid/main.css';
@import '~@fullcalendar/timegrid/main.css';

.fc-button {
  height: 2.4em;
}

.card-head {
  font-size: 1.3em;
}
</style>


// WEBPACK FOOTER //
// SocialPostCalendar.vue?60720312